import { Stack, Theme } from '@mui/material';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import Text from '../../text';
import { createUseStyles } from 'react-jss';
import Panel from '@/components/panel';
import PanelTitle from '@/components/panel/panel-title';
import PanelContent from '@/components/panel/panel-content';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import ScrollAnchor from '@/components/scroll-anchor';
import { ScrollAnchorId } from '@/components/scroll-anchor/helpers';
import { IconType } from '@/components/icon/helpers';

interface Props {
  title: string;
  children: ReactNode;
  locked?: boolean;
  disabled?: boolean;
  tooltip?: string;
  icon?: IconType;
  scrollId?: ScrollAnchorId;
}

const useStyles = createUseStyles((theme: Theme) => ({
  formSection: {
    position: 'relative',
    marginBottom: 30,
    '&.disabled': {
      backgroundColor: theme.palette.lightGray,
    },
    '& span.tooltip-children': {
      lineHeight: '70%',
    },
  },
}));

const FormSection: FC<Props> = ({ children, title, locked, disabled, tooltip, icon, scrollId }) => {
  const styles = useStyles();
  const hasScrollId = !!scrollId;
  const panelDisabled = disabled || locked;
  const compClass = classNames(styles.formSection, { disabled: panelDisabled });

  return (
    <Panel className={compClass}>
      {hasScrollId && <ScrollAnchor scrollId={scrollId} />}
      <PanelTitle disabled={panelDisabled}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" flexGrow={1} alignItems="center" gap={1}>
            {icon && <Icon name={icon} size="small" />}
            <Text size="large">{title}</Text>
          </Stack>
          {locked && (
            <Tooltip title={tooltip} disabled={!tooltip}>
              <Icon name="lock" size="small" />
            </Tooltip>
          )}
        </Stack>
      </PanelTitle>
      <PanelContent>{children}</PanelContent>
    </Panel>
  );
};

export default FormSection;
